import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClassesService } from 'src/app/services/classes.service';
import { MajorsService } from 'src/app/services/majors.service';

interface ClassItem {
  id: string;
  name: string;
  major: { id: string; name: string } | null;
}

@Component({
  selector: 'app-classes',
  templateUrl: './classes.component.html',
  styleUrls: ['./classes.component.css']
})
export class ClassesComponent {
  items: ClassItem[] = [];
  filteredItems: ClassItem[] = [];
  majors: any[] = [];
  selected: ClassItem[] = [];
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selectedId = '';
  isError = false;
  errorMessage = '';
  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    major: new FormControl('')
  });

  constructor(
    private classesService: ClassesService,
    private majorsService: MajorsService
  ) { }

  async ngOnInit() {
    await Promise.all([this.fetchItems(), this.fetchMajors()]);
  }

  async fetchItems() {
    this.items = await this.classesService.list();
    this.applyFilter();
  }

  async fetchMajors() {
    this.majors = await this.majorsService.list();
  }

  get paginatedItems(): ClassItem[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
  }

  get pageStart(): number {
    return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length);
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item =>
          item.name.toLowerCase().includes(term) ||
          item.major?.name?.toLowerCase().includes(term)
        )
      : [...this.items];
    this.pageIndex = 0;
  }

  onSearchChange() {
    this.applyFilter();
  }

  onPageSizeChange(size: string) {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
    }
  }

  isSelected(item: ClassItem): boolean {
    return this.selected.some(selected => selected.id === item.id);
  }

  toggleSelection(item: ClassItem, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item)
      ? this.selected.filter(selected => selected.id !== item.id)
      : [...this.selected, item];
  }

  clearSelection() {
    this.selected = [];
  }

  onAdd() {
    this.form.enable();
    this.mode = 'add';
    this.selectedId = '';
    this.form.reset();
    this.isError = false;
    this.open = true;
  }

  onEdit() {
    this.form.enable();
    this.mode = 'edit';
    this.selectedId = this.selected[0].id;
    this.form.patchValue({
      name: this.selected[0].name,
      major: this.selected[0].major?.id || ''
    });
    this.isError = false;
    this.open = true;
  }

  openRow(item: ClassItem) {
    this.form.enable();
    this.mode = 'view';
    this.selectedId = item.id;
    this.form.patchValue({
      name: item.name,
      major: item.major?.id || ''
    });
    this.isError = false;
    this.open = true;
    this.form.disable();
  }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'CLASSES.DELETE_ONE' : 'CLASSES.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.selected[0].name }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.classesService.delete(item.id);
        } catch (error: any) {
          this.showError(error?.error?.errorMessages?.[0]?.message || 'CLASSES.DELETE_FAILED');
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async submit() {
    if (this.mode === 'view') {
      return;
    }
    if (this.form.invalid) {
      this.showError('Name is required');
      return;
    }

    try {
      const body: { name: string; major?: string } = {
        name: this.form.value.name as string
      };
      if (this.form.value.major) {
        body.major = this.form.value.major as string;
      }
      if (this.mode === 'edit') {
        await this.classesService.update(this.selectedId, body);
      } else {
        await this.classesService.create(body);
      }
      this.open = false;
      this.selected = [];
      await this.fetchItems();
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'CLASSES.SAVE_FAILED');
    }
  }

  closeModal() {
    this.open = false;
    this.isError = false;
    this.form.enable();
  }

  showError(message: string) {
    this.isError = true;
    this.errorMessage = message;
  }
}
